import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const LABEL = {
  outside_09_21_india_time: "Outside 09:00–21:00 India time", no_valid_recorded_consent: "No valid recorded consent",
  do_not_call: "Recipient is on do-not-call", one_call_limit_used: "The single pilot call is already used",
  pilot_call_already_active: "A pilot call is already active", recipient_not_approved: "Recipient not approved",
  caller_id_not_approved: "Caller ID not approved", caller_id_account_credentials_missing: "Caller-ID account credentials missing",
  pilot_numbers_not_configured: "Pilot numbers not configured", pilot_disabled: "Pilot is not armed", public_base_missing: "Callback URL missing",
  armed_by_another_admin: "Pilot was armed by another admin; re-arm it yourself",
};
const anyhourText = (s) => (s.anyhour_exception
  ? `active for you (one attempt only; switches off after the first call${s.india_hours_ok ? "; not needed right now" : ""})`
  : "not available (normal 09:00–21:00 IST rule applies)");
const btn = "rounded-md px-3 py-1.5 text-sm font-semibold disabled:opacity-40";

function Checks({ s }) {
  const items = [...(s.blocked_by || []), ...(s.enabled ? [] : ["pilot_disabled"])];
  return (
    <div className="rounded-lg border border-slate-700 p-4 text-sm" data-testid="pilot-checks">
      <p className="mb-2 font-semibold text-slate-200">Safety checks</p>
      {items.length === 0 ? <p className="text-emerald-300" data-testid="pilot-checks-ok">All checks pass. Ready for one call.</p>
        : <ul className="list-disc pl-5 text-amber-300">{items.map((b) => <li key={b} data-testid={`pilot-block-${b}`}>{LABEL[b] || b}</li>)}</ul>}
      <p className="mt-3 text-xs text-slate-400">Caller +{s.from} → recipient ending {s.to_last4} · Server switch: {s.mode} · Armed: {s.armed ? `yes, until ${new Date(s.armed_until).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" })} IST` : "no"}</p>
      <p className="mt-1 text-xs text-slate-400" data-testid="pilot-anyhour">Any-hour exception: {anyhourText(s)}</p>
      <p className="mt-1 text-xs text-slate-400" data-testid="pilot-consent">Consent: {s.consent?.consent_at ? `recorded ${s.consent.consent_at}` : "not recorded"}</p>
    </div>
  );
}

function Calls({ calls, onHangup }) {
  if (!calls?.length) { return <p className="text-sm text-slate-400" data-testid="pilot-no-calls">No pilot call yet.</p>; }
  return (
    <table className="w-full text-sm" data-testid="pilot-calls">
      <thead><tr className="text-left text-slate-400"><th>Call</th><th>Status</th><th>Outcome</th><th>Duration</th><th>Cause</th><th /></tr></thead>
      <tbody>{calls.map((c) => (
        <tr key={c.id} className="border-t border-slate-800 text-slate-200" data-testid={`pilot-call-${c.id}`}>
          <td className="py-2">{c.id}</td><td>{c.status}</td><td>{c.outcome || "—"}</td><td>{c.duration_s ?? "—"}</td><td>{c.reason || c.hangup_cause || "—"}</td>
          <td>{["dialing", "ringing", "in_progress", "queued"].includes(c.status) &&
            <button type="button" className={`${btn} bg-rose-600 text-white`} onClick={() => onHangup(c.id)} data-testid={`pilot-hangup-${c.id}`}>Hang up now</button>}</td>
        </tr>))}</tbody>
    </table>
  );
}

export default function ShallyPilot() {
  const [s, setS] = useState(null);
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setS((await api.get("/platform/shally-pilot")).data); } catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  const run = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); if (ok) { const m = ok(r.data); (r.data?.dry_run && !r.data.would_dial ? toast.warning : toast.success)(m); } } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); load(); }
  };
  const key = () => `pilot_${new Date().toISOString().slice(0, 16)}`;
  if (!s) { return <p className="p-6 text-slate-400">Loading…</p>; }
  const ready = s.enabled && !(s.blocked_by || []).length;
  return (
    <div className="max-w-3xl space-y-5 p-6" data-testid="shally-pilot-page">
      <div><h1 className="text-2xl font-semibold text-white">Shally outbound pilot</h1>
        <p className="text-sm text-slate-400">One consent-based test call. 2-minute limit, no retry, no recording.</p></div>
      <Checks s={s} />
      <div className="flex flex-wrap gap-2">
        <button type="button" className={`${btn} bg-slate-700 text-white`} disabled={busy || s.mode !== "armable" || s.armed}
          onClick={() => run(() => api.post("/platform/shally-pilot/arm"), () => "Armed for 15 minutes")} data-testid="pilot-arm">Arm for 15 min</button>
        <button type="button" className={`${btn} bg-slate-700 text-white`} disabled={busy}
          onClick={() => run(() => api.post("/platform/shally-pilot/call", { recipient: "9028148783", idempotency_key: key(), dry_run: true }),
            (d) => (d.would_dial ? "Dry run: would dial now" : `Dry run blocked: ${(d.blocked_by || []).map((b) => LABEL[b] || b).join(", ")}`))} data-testid="pilot-dry-run">Dry run</button>
        <button type="button" className={`${btn} bg-emerald-600 text-white`} disabled={busy || !ready} onClick={() => setAsk(true)} data-testid="pilot-place-call">Place the one test call</button>
        {s.armed && <button type="button" className={`${btn} border border-slate-600 text-slate-200`} disabled={busy}
          onClick={() => run(() => api.delete("/platform/shally-pilot/arm"), () => "Disarmed")} data-testid="pilot-disarm">Disarm</button>}
      </div>
      <Calls calls={s.calls} onHangup={(id) => run(() => api.post(`/platform/shally-pilot/${id}/hangup`), () => "Hang-up requested")} />
      <AlertDialog open={ask} onOpenChange={(o) => { if (!o && !busy) { setAsk(false); } }}>
        <AlertDialogContent data-testid="pilot-confirm-dialog">
          <AlertDialogHeader><AlertDialogTitle>Place one real call?</AlertDialogTitle>
            <AlertDialogDescription>Shally will call the consenting recipient ending {s.to_last4} from +{s.from}. This uses the single pilot call; it cannot be retried.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pilot-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="pilot-confirm-call" onClick={() => { setAsk(false); run(() => api.post("/platform/shally-pilot/call", { recipient: "9028148783", idempotency_key: key(), dry_run: false, confirm: true }), (d) => (d.duplicate ? "Already placed" : "Call requested from Plivo")); }}>Place call</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
